/** @module Auth0BetaOptInFeatureFlag */
/* eslint-disable import/extensions */
import Auth0BetaOptInWithUserContext from './auth0-beta-opt-in-feature-user-context-base.mjs';
import Auth0FeatureFlagHelper from '../auth0-feature-flag-helper.mjs';
import Auth0UserSessionHelper from '../auth0-session-helper.mjs';
import logger from '../logging/auth0-logger.mjs';

class Auth0BetaOptInFeatureFlag extends Auth0BetaOptInWithUserContext {
  constructor(options) {
    super(options);
    console.log(`Auth0BetaOptInFeatureFlag init for flag => "${JSON.stringify(options)}"`);
    this._ldClientSideId = options.ldClientSideId;
    this._auth0TenantId = options.auth0Tenant;
    this._supportsAnonymousEval = options.supportsAnonymousEval ?? true;

    this._init();
  }

  _init() {
    super._init();

    if (Auth0UserSessionHelper.isLoggedIn) {
      this._buildUserContextFromAuthState();
      // this is a truthful record of context so always record this for future context use (when logged out)
      this._addUserContextToPersistedState();
    } else {
      this._restoreStateFromDisk();
    }
  }

  _validate() {
    super._validate();

    if (!this._ldClientSideId) {
      throw new Error(`Cannot initialize beta feature "${this._featureId}" because it supports LD feature flags, but does not have access to a feature flag provider. In the construction of the object provide a value for '_ldClientSideId'`);
    }
  }

  _getNewFeatureFlagHelper() {
    return new Auth0FeatureFlagHelper({
      auth0Tenant: this._auth0TenantId,
      clientSideId: this._ldClientSideId,
    });
  }

  get _ffProvider() {
    if (!this.__ffProvider) {
      this.__ffProvider = this._getNewFeatureFlagHelper();
    }
    return this.__ffProvider;
  }

  optIn() {
    // this._buildUserContextFromAuthState();
    // NOTE: Never opt in if this._actualUserContextData is missing (i.e. don't allow opt in from a user context that found in a cookie)
    throw new Error('User opt-in for feature flag backed features is not supported');
  }

  optOut() {
    // this._buildUserContextFromAuthState();
    // NOTE: Never opt out if this._actualUserContextData is missing (i.e. don't allow opt out from a user context that found in a cookie)
    throw new Error('User opt-out for feature flag backed features is not supported');
  }

  isOptedIn = async () => {
    this._buildUserContextFromAuthState();
    if (!Auth0UserSessionHelper.isLoggedIn) {
      if (!this._supportsAnonymousEval) {
        throw new Error(`Feature "${this._featureId}" is not configured to support anonymous opt in state evaluations. Log in first then try again (or configure this object to support anonymous users).`);
      }
      const val = await this._isOptedInAnonymous();
      return val;
    }

    // otherwise, logged in so use the current user context
    const val = await this._isOptedInUserContext();
    return val;
  };

  _isOptedInUserContext = async () => {
    console.log(`start isOptedInUserContext for "${this._featureId}"...`);
    const userContext = this._toUserContext(this._actualUserContextData.sellerId, this._actualUserContextData);
    await this._ffProvider.initialize(userContext);
    const val = await this._ffProvider.eval(this._featureId, false);
    console.log(`Feature flag "${this._featureId}" evaluated to "${val}"...`);
    return val;
  };

  _isOptedInAnonymous = async () => {
    console.log(`start isOptedInAnonymous for "${this._featureId}"...`);
    const userContextSet = this._buildUserContextList();
    const evalPromises = userContextSet.map(async (uc) => {
      const ld = this._getNewFeatureFlagHelper();
      console.log(`Evaluating feature flag "${this._featureId}" for "${uc.key}"...`);
      try {
        await ld.initialize(uc);
      } catch (error) {
        logger.warn(`LDClient with ID ${this._ldClientSideId} failed to initialize`, error);
        return Promise.resolve(false);
      }
      return ld.eval(this._featureId, false);
    });

    const resolved = await Promise.all(evalPromises);
    console.log(`Feature flag "${this._featureId}" evaluation complete => "${resolved.join('; ')}"...`);
    // OR-based return (i.e. if any in user history was opted in, this will return true)
    const val = resolved.some(r => r === true);
    console.log(`Feature flag "${this._featureId}" evaluated to "${val}"...`);
    return val;
  };
}

export default Auth0BetaOptInFeatureFlag;
